/**
 * Knowledge Curation Page (Enterprise Only)
 */

import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Modal, TextInput, Textarea, Select, Badge, Pagination } from 'flowbite-react';
import { HiPlus, HiPencil, HiTrash, HiSearch, HiEye, HiArchive } from 'react-icons/hi';
import { useStatus } from '../contexts/StatusContext';
import { enterpriseApi } from '../api/enterprise';
import type { CuratedKnowledge } from '../types';

interface KnowledgeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<CuratedKnowledge>) => Promise<void>;
  knowledge?: CuratedKnowledge | null;
}

const KnowledgeModal: React.FC<KnowledgeModalProps> = ({ isOpen, onClose, onSave, knowledge }) => {
  const [title, setTitle] = useState(knowledge?.title || '');
  const [content, setContent] = useState(knowledge?.content || '');
  const [category, setCategory] = useState(knowledge?.category || '');
  const [tags, setTags] = useState(knowledge?.tags?.join(', ') || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (knowledge) {
      setTitle(knowledge.title);
      setContent(knowledge.content);
      setCategory(knowledge.category || '');
      setTags(knowledge.tags?.join(', ') || '');
    } else {
      setTitle('');
      setContent('');
      setCategory('');
      setTags('');
    }
  }, [knowledge]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await onSave({
        title,
        content,
        category: category || undefined,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save knowledge item');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={isOpen} onClose={onClose} size="xl">
      <Modal.Header>{knowledge ? 'Edit Knowledge Item' : 'Create Knowledge Item'}</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-700 bg-red-100 rounded-md">{error}</div>
          )}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <TextInput
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Knowledge item title"
              required
            />
          </div>
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
              Content
            </label>
            <Textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Knowledge content (markdown supported)"
              rows={10}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <TextInput
                id="category"
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g., Architecture, Testing"
              />
            </div>
            <div>
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
                Tags (comma-separated)
              </label>
              <TextInput
                id="tags"
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="react, typescript, testing"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2 pt-4">
            <Button color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : knowledge ? 'Save Changes' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

const KnowledgeCuration: React.FC = () => {
  const { hasFeature } = useStatus();
  const [items, setItems] = useState<CuratedKnowledge[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'draft' | 'published' | 'archived' | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CuratedKnowledge | null>(null);

  // Check feature access
  if (!hasFeature('knowledge_curation')) {
    return (
      <Card className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Enterprise Feature</h2>
        <p className="text-gray-500 mb-4">
          Knowledge curation is available on the Enterprise plan.
        </p>
        <a
          href="/subscriptions"
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-mindmeld-primary rounded-md hover:bg-blue-700"
        >
          Upgrade to Enterprise
        </a>
      </Card>
    );
  }

  useEffect(() => {
    loadKnowledge();
  }, [page, statusFilter]);

  const loadKnowledge = async () => {
    setIsLoading(true);
    try {
      const response = await enterpriseApi.getKnowledge({
        page,
        pageSize: 20,
        status: statusFilter || undefined,
      });

      if (response.success && response.data) {
        setItems(response.data.records);
        setTotalPages(Math.ceil(response.data.total / response.data.pageSize));
      } else {
        setError(response.error || 'Failed to load knowledge items');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load knowledge items');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (data: Partial<CuratedKnowledge>) => {
    if (selectedItem) {
      const response = await enterpriseApi.updateKnowledge(selectedItem.knowledge_id, data);
      if (!response.success) {
        throw new Error(response.error || 'Failed to update knowledge item');
      }
    } else {
      const response = await enterpriseApi.createKnowledge(data as any);
      if (!response.success) {
        throw new Error(response.error || 'Failed to create knowledge item');
      }
    }

    await loadKnowledge();
    setSelectedItem(null);
  };

  const handlePublish = async (item: CuratedKnowledge) => {
    try {
      const response = await enterpriseApi.updateKnowledge(item.knowledge_id, {
        status: 'published',
      });
      if (response.success) {
        await loadKnowledge();
      } else {
        setError(response.error || 'Failed to publish');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish');
    }
  };

  const handleArchive = async (item: CuratedKnowledge) => {
    if (!confirm('Are you sure you want to archive this knowledge item?')) {
      return;
    }

    try {
      const response = await enterpriseApi.updateKnowledge(item.knowledge_id, {
        status: 'archived',
      });
      if (response.success) {
        await loadKnowledge();
      } else {
        setError(response.error || 'Failed to archive');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive');
    }
  };

  const handleDelete = async (item: CuratedKnowledge) => {
    if (!confirm('Are you sure you want to delete this knowledge item?')) {
      return;
    }

    try {
      const response = await enterpriseApi.deleteKnowledge(item.knowledge_id);
      if (response.success) {
        await loadKnowledge();
      } else {
        setError(response.error || 'Failed to delete');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'success';
      case 'draft':
        return 'warning';
      case 'archived':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const filteredItems = items.filter(
    (item) =>
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.tags?.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Knowledge Curation</h1>
          <p className="mt-1 text-sm text-gray-500">
            Curate and manage organizational knowledge
          </p>
        </div>
        <Button onClick={() => { setSelectedItem(null); setIsModalOpen(true); }}>
          <HiPlus className="mr-2 h-4 w-4" />
          New Item
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 text-red-700 bg-red-100 rounded-md">{error}</div>
      )}

      {/* Filters */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <TextInput
              icon={HiSearch}
              placeholder="Search knowledge..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </Select>
          </div>
        </div>
      </Card>

      {/* Knowledge Items */}
      <Card>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mindmeld-primary"></div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-sm font-medium text-gray-900">No knowledge items</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating your first knowledge item.
            </p>
          </div>
        ) : (
          <>
            <Table>
              <Table.Head>
                <Table.HeadCell>Title</Table.HeadCell>
                <Table.HeadCell>Category</Table.HeadCell>
                <Table.HeadCell>Tags</Table.HeadCell>
                <Table.HeadCell>Status</Table.HeadCell>
                <Table.HeadCell>Curator</Table.HeadCell>
                <Table.HeadCell>Actions</Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {filteredItems.map((item) => (
                  <Table.Row key={item.knowledge_id} className="bg-white">
                    <Table.Cell>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{item.title}</p>
                        <p className="text-xs text-gray-500 truncate max-w-xs">
                          {item.content.substring(0, 100)}...
                        </p>
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      {item.category && (
                        <Badge color="blue">{item.category}</Badge>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex flex-wrap gap-1">
                        {item.tags?.slice(0, 3).map((tag) => (
                          <Badge key={tag} color="gray" size="sm">
                            {tag}
                          </Badge>
                        ))}
                        {item.tags && item.tags.length > 3 && (
                          <Badge color="gray" size="sm">+{item.tags.length - 3}</Badge>
                        )}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge color={getStatusBadgeColor(item.status)}>
                        {item.status}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-sm text-gray-500">{item.curator_email}</span>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex space-x-2">
                        <button
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="Edit"
                          onClick={() => { setSelectedItem(item); setIsModalOpen(true); }}
                        >
                          <HiPencil className="h-4 w-4" />
                        </button>
                        {item.status === 'draft' && (
                          <button
                            className="p-1 text-gray-400 hover:text-green-600"
                            title="Publish"
                            onClick={() => handlePublish(item)}
                          >
                            <HiEye className="h-4 w-4" />
                          </button>
                        )}
                        {item.status !== 'archived' && (
                          <button
                            className="p-1 text-gray-400 hover:text-amber-600"
                            title="Archive"
                            onClick={() => handleArchive(item)}
                          >
                            <HiArchive className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Delete"
                          onClick={() => handleDelete(item)}
                        >
                          <HiTrash className="h-4 w-4" />
                        </button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-4">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </Card>

      {/* Knowledge Modal */}
      <KnowledgeModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedItem(null);
        }}
        onSave={handleSave}
        knowledge={selectedItem}
      />
    </div>
  );
};

export default KnowledgeCuration;
